import React from 'react';
import {useMatches} from '@remix-run/react';

//components
import Container from 'components/Container';
import SanityImage from 'components/SanityImage';
import {Link} from 'components/Link';

//types
import type {SanityModuleBlogSection} from 'domains/modules';
import type {I18nLocale} from '~/lib/type';

interface Props {
  module: SanityModuleBlogSection;
}

/**
 * Homepage "最新情報 / Latest News" section: the three most recent blog posts
 * as a Squarespace-style card row (image, category tag + date, title,
 * excerpt), followed by a dated list of older posts (more_blogs) so shoppers
 * can browse updates from other dates without leaving the homepage. The
 * heading is hardcoded so it reads "最新情報" regardless of the Sanity
 * section_title.
 */
const BlogSection: React.FC<Props> = ({module}) => {
  const [root] = useMatches();
  const selectedLocale = root?.data?.selectedLocale as I18nLocale | undefined;
  const isZh = selectedLocale?.language !== 'EN';

  const posts = (module.recent_blogs ?? []).slice(0, 3);
  const moreItems = module.more_blogs ?? [];
  if (posts.length === 0) return null;

  return (
    <section className="section--blogs-section py-7">
      <Container className="bg-hk-gray py-7 lg:py-14">
        <header className="mb-10 text-center lg:mb-14">
          <h2 className="text-xl md:text-section font-bold uppercase tracking-wide">
            {isZh ? '最新情報' : 'Latest News'}
          </h2>
        </header>
        <div className="mx-auto w-full lg:px-8">
          <ul className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-10">
            {posts.map((b) => (
              <li key={b._rev}>
                <article className="blog group h-full">
                  <Link
                    to={`/blogs/${b.blog_category?.slug?.current}/${b?.slug?.current}`}
                    className="flex h-full flex-col overflow-hidden rounded-lg bg-white shadow-sm transition-shadow duration-200 hover:shadow-md"
                  >
                    <div className="blog__thumbnail aspect-[3/2] overflow-hidden">
                      <SanityImage
                        layout="responsive"
                        sizes={['33vw, 100vw']}
                        src={b.blog_thumbnail?.asset._ref}
                        alt={b.blog_title ?? ''}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <div className="mb-3 flex flex-wrap items-center gap-3">
                        {b.blog_category?.category_title && (
                          <span className="inline-block rounded-full bg-hk-orange px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                            {b.blog_category.category_title}
                          </span>
                        )}
                        {b.blog_published_date && (
                          <time className="text-xs text-gray-500">
                            {b.blog_published_date}
                          </time>
                        )}
                      </div>
                      <h3 className="mb-2 line-clamp-2 text-lg font-semibold leading-snug transition-colors duration-200 group-hover:text-hk-orange">
                        {b.blog_title}
                      </h3>
                      <p className="line-clamp-3 text-sm text-gray-600">
                        {b.blog_description}
                      </p>
                    </div>
                  </Link>
                </article>
              </li>
            ))}
          </ul>

          {moreItems.length > 0 && (
            <div className="mt-12">
              <h3 className="mb-4 border-l-4 border-hk-orange pl-3 text-lg font-bold">
                {isZh ? '更多消息' : 'More Updates'}
              </h3>
              <div className="rounded-lg border border-gray-200 bg-white">
                <ul className="max-h-[360px] divide-y divide-gray-100 overflow-y-auto">
                  {moreItems.map((b) => (
                    <li key={b._rev}>
                      <Link
                        to={`/blogs/${b.blog_category?.slug?.current}/${b?.slug?.current}`}
                        className="group flex flex-col gap-1 px-5 py-3.5 transition-colors duration-150 hover:bg-hk-gray sm:flex-row sm:items-baseline sm:gap-5"
                      >
                        {b.blog_published_date && (
                          <time className="shrink-0 text-sm tabular-nums text-gray-500">
                            {b.blog_published_date}
                          </time>
                        )}
                        <span className="line-clamp-1 text-sm text-gray-800 transition-colors duration-150 group-hover:text-hk-orange">
                          {b.blog_title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="mt-10 text-center">
            <Link
              to="/blogs"
              className="inline-flex items-center gap-1 rounded-sm border border-black px-8 py-3 text-sm font-semibold transition-colors duration-200 hover:bg-black hover:text-white"
            >
              {isZh ? '查看更多' : 'View all'}
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default BlogSection;
